import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ActivityIndicator,
	Image,
	ImageSourcePropType,
	Modal,
	Platform,
	ScrollView,
	StyleSheet,
	Text,
	View,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import {
	PlayerIconPng,
	RankingsFirstPlacePng,
	RankingsSecondPlacePng,
	RankingsThirdPlacePng,
} from '../../../assets';
import {
	DuelHudHistorySvg,
	DuelHudInfoSvg,
	DuelResultChartSvg,
	DuelResultClockPng,
	DuelResultCrownPng,
	DuelResultDecisionPng,
	DuelResultEfficiencyPng,
	DuelResultHomeSvg,
	DuelResultOutcomePng,
	DuelResultPlayerPng,
	DuelResultRatingPng,
	DuelResultSeasonPng,
	DuelResultShieldSwordsPng,
	DuelResultSparkle2Svg,
	DuelResultSparkle3Svg,
	DuelResultSparkleSvg,
	DuelResultTrophyPng,
} from '../../../assets/duels';
import type {
	DuelHubResponse,
	DuelResultLeaderboardEntryDto,
	DuelResultResponse,
} from '../../../services/api';
import Portal from '../../../shared/Portal';
import {
	duelResultBlur,
	duelResultFocus,
	duelResultRetry,
} from '../../../store/duels/duelsActions';
import {
	selectDuelResult,
	selectDuelResultErrorKey,
	selectDuelsHub,
} from '../../../store/duels/duelsReducer';
import { getAvatarUrl } from '../../../utils/axiosInstance';
import { GradientFill } from '../common/GradientFill';
import { Pressable, TouchableOpacity } from '../common/Touchable';
import { useDuelHudLayout } from './DuelHud';
import {
	DUEL_HUD_COUNTER_FILL,
	DUEL_HUD_FONT,
	DUEL_HUD_HISTORY_FILL,
	DUEL_HUD_SHELL_FILL,
	DUEL_MODAL_ORIENTATIONS,
	DUEL_RESULT_BACKDROP,
	DUEL_RESULT_LAYOUT,
	duelHudColors,
	snapDesktopFont,
} from './duelHudTheme';
import { DUEL_SCORE_SOURCES, DuelScoreSourceKey } from './DuelScoreSourceIcon';
import { duelText } from './duelsText';
import { deltaColor, divisionBadge, duelColors, formatScore, formatSigned } from './duelsTheme';

const PLACE_MEDALS: Record<number, ImageSourcePropType> = {
	1: RankingsFirstPlacePng,
	2: RankingsSecondPlacePng,
	3: RankingsThirdPlacePng,
};

const SOURCE_ART: Record<DuelScoreSourceKey, ImageSourcePropType> = {
	outcome: DuelResultOutcomePng,
	decisionQuality: DuelResultDecisionPng,
	riskManagement: DuelResultShieldSwordsPng,
	timeBonus: DuelResultEfficiencyPng,
};

const GOLD = '#FFD766';
const GOLD_TEXT = '#FFE9A8';

type ResultState = 'loading' | 'provisional' | 'settling' | 'settled';

const resultState = (result: DuelResultResponse | null): ResultState => {
	if (!result) return 'loading';
	if (result.settlement) return 'settled';
	return result.provisional ? 'provisional' : 'settling';
};

type Sizing = { s: number; f: (size: number) => number };

const shellBorder = (s: number) => Math.max(1, 1.5 * s);

const Card = ({
	children,
	sizing,
	style,
}: {
	children: React.ReactNode;
	sizing: Sizing;
	style?: object;
}) => {
	const radius = 12 * sizing.s;
	return (
		<View
			style={[
				styles.card,
				{
					borderRadius: radius,
					borderWidth: Math.max(1, sizing.s),
					padding: 10 * sizing.s,
					rowGap: 8 * sizing.s,
				},
				style,
			]}
		>
			<GradientFill stops={DUEL_HUD_COUNTER_FILL} radius={radius} />
			{children}
		</View>
	);
};

const SectionTitle = ({
	text,
	sizing,
	onInfo,
}: {
	text: string;
	sizing: Sizing;
	onInfo?: () => void;
}) => (
	<View style={[styles.row, { columnGap: 6 * sizing.s }]}>
		<Text
			numberOfLines={1}
			style={[
				styles.black,
				styles.shrink,
				{
					color: duelHudColors.textPrimary,
					fontSize: sizing.f(14),
					letterSpacing: 0.8 * sizing.s,
				},
			]}
		>
			{text}
		</Text>
		{onInfo ? (
			<Pressable onPress={onInfo} accessibilityRole='button' hitSlop={8} style={styles.pushRight}>
				<DuelHudInfoSvg width={16 * sizing.s} height={16 * sizing.s} />
			</Pressable>
		) : null}
	</View>
);

const Tile = ({
	icon,
	value,
	valueColor,
	label,
	caption,
	sizing,
	iconAbove = false,
	valueSize = 18,
}: {
	icon: React.ReactNode;
	value: string;
	valueColor: string;
	label: string;
	caption?: string;
	sizing: Sizing;
	iconAbove?: boolean;
	valueSize?: number;
}) => {
	const radius = 10 * sizing.s;
	return (
		<View
			style={[
				styles.tile,
				{
					borderRadius: radius,
					borderWidth: Math.max(1, sizing.s),
					paddingVertical: 8 * sizing.s,
					paddingHorizontal: 4 * sizing.s,
					rowGap: 2 * sizing.s,
				},
			]}
		>
			<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={radius} />
			<View
				style={[
					iconAbove ? styles.column : styles.row,
					styles.centered,
					{ columnGap: 4 * sizing.s, rowGap: 2 * sizing.s },
				]}
			>
				{icon}
				<Text
					numberOfLines={1}
					style={[
						styles.black,
						styles.shrink,
						{ color: valueColor, fontSize: sizing.f(valueSize) },
					]}
				>
					{value}
				</Text>
			</View>
			<Text
				numberOfLines={2}
				style={[
					styles.medium,
					styles.centeredText,
					{ color: duelHudColors.textSecondary, fontSize: sizing.f(10) },
				]}
			>
				{label}
			</Text>
			{caption ? (
				<Text
					numberOfLines={1}
					style={[
						styles.semibold,
						styles.centeredText,
						{ color: duelHudColors.time, fontSize: sizing.f(10) },
					]}
				>
					{caption}
				</Text>
			) : null}
		</View>
	);
};

const LeaderRow = ({ row, sizing }: { row: DuelResultLeaderboardEntryDto; sizing: Sizing }) => {
	const { t } = useTranslation();
	const s = sizing.s;
	const medal = PLACE_MEDALS[row.rank];
	const avatar = row.avatarFileId ? { uri: getAvatarUrl(row.avatarFileId) } : PlayerIconPng;
	return (
		<View
			style={[
				styles.row,
				{
					height: 40 * s,
					columnGap: 10 * s,
					paddingHorizontal: 8 * s,
					borderRadius: 8 * s,
					backgroundColor: row.isYou ? duelColors.youRow : 'transparent',
					borderWidth: row.isYou ? Math.max(1, s) : 0,
					borderColor: duelColors.youBorder,
				},
			]}
		>
			<View style={[styles.centered, { width: 24 * s }]}>
				{medal ? (
					<Image source={medal} resizeMode='contain' style={{ width: 20 * s, height: 26 * s }} />
				) : (
					<Text style={[styles.bold, { color: duelHudColors.textPrimary, fontSize: sizing.f(14) }]}>
						{row.rank}
					</Text>
				)}
			</View>
			<View>
				<Image
					source={avatar}
					style={{
						width: 28 * s,
						height: 28 * s,
						borderRadius: 14 * s,
						borderWidth: Math.max(1, 1.5 * s),
						borderColor: GOLD,
					}}
				/>
				{row.rank === 1 ? (
					<View style={[styles.crown, { top: -9 * s }]}>
						<Image
							source={DuelResultCrownPng}
							resizeMode='contain'
							style={{ width: 16 * s, height: 12 * s }}
						/>
					</View>
				) : null}
			</View>
			<Text
				numberOfLines={1}
				style={[
					styles.semibold,
					styles.grow,
					{ color: duelHudColors.textPrimary, fontSize: sizing.f(14) },
				]}
			>
				{row.isYou ? duelText(t, 'T_SITE_DUELS_YOU') : (row.username ?? '—')}
			</Text>
			<Text
				style={[
					styles.black,
					{ color: row.isYou ? GOLD_TEXT : duelHudColors.textPrimary, fontSize: sizing.f(16) },
				]}
			>
				{formatScore(row.displayScore)}
			</Text>
		</View>
	);
};

type PanelProps = {
	result: DuelResultResponse | null;
	errorKey?: string;
	hub: DuelHubResponse | null;
	sizing: Sizing;
	onRetry: () => void;
	onClose: () => void;
	onSettled: () => void;
	onHistory: () => void;
	onBreakdown: () => void;
};

const Header = ({
	result,
	sizing,
	onHistory,
}: Pick<PanelProps, 'result' | 'sizing' | 'onHistory'>) => {
	const { t } = useTranslation();
	const s = sizing.s;
	const state = resultState(result);
	const final = state === 'settled' || state === 'settling';
	const subtitleKey =
		state === 'settled'
			? 'T_SITE_DUELS_RESULT_FINAL_NOTE'
			: state === 'settling'
				? 'T_SITE_DUELS_SETTLING'
				: 'T_SITE_DUELS_RESULT_CONFIRM_NOTE';
	const notRanked = !!result && (!result.eligible || result.rank === null);
	const historyRadius = 10 * s;

	return (
		<View style={{ rowGap: 8 * s }}>
			<View style={[styles.row, { columnGap: 10 * s }]}>
				<View style={{ width: 64 * s, height: 58 * s }}>
					<Image
						source={DuelResultTrophyPng}
						resizeMode='contain'
						style={[styles.absolute, { left: 6 * s, top: 4 * s, width: 52 * s, height: 52 * s }]}
					/>
					<View pointerEvents='none' style={[styles.absolute, { left: 0, top: 22 * s }]}>
						<DuelResultSparkleSvg width={14.6667 * s} height={14.6667 * s} />
					</View>
					<View pointerEvents='none' style={[styles.absolute, { right: 2 * s, top: 0 }]}>
						<DuelResultSparkle2Svg width={13 * s} height={13 * s} />
					</View>
					<View pointerEvents='none' style={[styles.absolute, { right: 0, top: 28 * s }]}>
						<DuelResultSparkle3Svg width={18 * s} height={18 * s} />
					</View>
				</View>
				<View style={[styles.grow, { rowGap: 6 * s }]}>
					<Text
						numberOfLines={2}
						adjustsFontSizeToFit
						minimumFontScale={0.75}
						style={[
							styles.black,
							{ color: GOLD_TEXT, fontSize: sizing.f(20), letterSpacing: 0.6 * s },
						]}
					>
						{duelText(t, 'T_SITE_DUELS_RESULT_RUN_COMPLETE')}
					</Text>
					<View
						style={[
							styles.pill,
							styles.row,
							{
								columnGap: 4 * s,
								borderWidth: Math.max(1, s),
								paddingHorizontal: 8 * s,
								paddingVertical: 3 * s,
								borderColor: final ? duelHudColors.pillBorder : GOLD,
								backgroundColor: final ? duelHudColors.pillFill : 'rgba(90,40,10,0.6)',
							},
						]}
					>
						{final ? null : (
							<Image
								source={DuelResultClockPng}
								resizeMode='contain'
								style={{ width: 12 * s, height: 12 * s }}
							/>
						)}
						<Text
							numberOfLines={1}
							style={[
								styles.bold,
								styles.shrink,
								{
									color: final ? duelHudColors.success : GOLD_TEXT,
									fontSize: sizing.f(10),
									letterSpacing: 0.6 * s,
								},
							]}
						>
							{duelText(t, final ? 'T_SITE_DUELS_RESULT_FINAL' : 'T_SITE_DUELS_RESULT_PROVISIONAL')}
						</Text>
					</View>
				</View>
				<TouchableOpacity
					onPress={onHistory}
					accessibilityRole='button'
					style={[
						styles.row,
						styles.centered,
						{
							height: 36 * s,
							paddingHorizontal: 10 * s,
							columnGap: 6 * s,
							borderRadius: historyRadius,
							borderWidth: Math.max(1, s),
							borderColor: duelHudColors.historyBorder,
						},
					]}
				>
					<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={historyRadius} />
					<View pointerEvents='none'>
						<DuelHudHistorySvg width={18 * s} height={18 * s} />
					</View>
					<Text
						style={[styles.semibold, { color: duelHudColors.textPrimary, fontSize: sizing.f(12) }]}
					>
						{duelText(t, 'T_SITE_DUELS_HUD_HISTORY')}
					</Text>
				</TouchableOpacity>
			</View>
			<Text style={[styles.medium, { color: duelHudColors.textSecondary, fontSize: sizing.f(12) }]}>
				{duelText(t, subtitleKey)}
			</Text>
			{notRanked ? (
				<Text style={[styles.semibold, { color: duelHudColors.time, fontSize: sizing.f(12) }]}>
					{duelText(t, 'T_SITE_DUELS_NOT_RANKED_BODY')}
				</Text>
			) : null}
		</View>
	);
};

const Leaderboard = ({ result, sizing }: Pick<PanelProps, 'result' | 'sizing'>) => {
	const { t } = useTranslation();
	const s = sizing.s;
	const label = [styles.medium, { color: duelHudColors.textSecondary, fontSize: sizing.f(11) }];
	if (!result?.leaderboard.length) {
		return null;
	}
	return (
		<Card sizing={sizing}>
			<View style={[styles.row, { columnGap: 10 * s, paddingHorizontal: 8 * s }]}>
				<Text style={[label, { width: 24 * s, textAlign: 'center' }]}>#</Text>
				<View style={[styles.row, styles.grow, { columnGap: 4 * s }]}>
					<Image
						source={DuelResultPlayerPng}
						resizeMode='contain'
						style={{ width: 11 * s, height: 12 * s }}
					/>
					<Text style={label}>{duelText(t, 'T_SITE_DUELS_RESULT_PLAYER')}</Text>
				</View>
				<View style={[styles.row, { columnGap: 4 * s }]}>
					<Image
						source={DuelResultShieldSwordsPng}
						resizeMode='contain'
						style={{ width: 14 * s, height: 14 * s }}
					/>
					<Text style={label}>{duelText(t, 'T_SITE_DUELS_DUEL_SCORE')}</Text>
				</View>
			</View>
			{result.leaderboard.map(row => (
				<LeaderRow key={`${row.rank}-${row.playerId}`} row={row} sizing={sizing} />
			))}
		</Card>
	);
};

const Breakdown = ({
	result,
	sizing,
	onBreakdown,
}: Pick<PanelProps, 'result' | 'sizing' | 'onBreakdown'>) => {
	const { t } = useTranslation();
	const breakdown = result?.breakdown ?? null;
	return (
		<Card sizing={sizing}>
			<SectionTitle
				text={duelText(t, 'T_SITE_DUELS_RESULT_BREAKDOWN')}
				sizing={sizing}
				onInfo={breakdown ? onBreakdown : undefined}
			/>
			<View style={[styles.row, { columnGap: 6 * sizing.s, alignItems: 'stretch' }]}>
				{DUEL_SCORE_SOURCES.map(source => {
					const value = breakdown ? breakdown[source.key] : null;
					return (
						<Tile
							key={source.key}
							sizing={sizing}
							iconAbove
							icon={
								<Image
									source={SOURCE_ART[source.key]}
									resizeMode='contain'
									style={{ width: 22 * sizing.s, height: 22 * sizing.s }}
								/>
							}
							value={value === null ? '—' : formatSigned(value)}
							valueColor={value === null ? duelHudColors.textSecondary : deltaColor(value)}
							label={duelText(t, source.title)}
						/>
					);
				})}
			</View>
		</Card>
	);
};

const Progression = ({ result, hub, sizing }: Pick<PanelProps, 'result' | 'hub' | 'sizing'>) => {
	const { t } = useTranslation();
	const s = sizing.s;
	const settlement = result?.settlement ?? null;
	const unranked = !!result && !result.ranked;
	const pendingCaption =
		settlement || unranked ? undefined : duelText(t, 'T_SITE_DUELS_RESULT_PENDING');
	const icon = (source: ImageSourcePropType) => (
		<Image source={source} resizeMode='contain' style={{ width: 18 * s, height: 18 * s }} />
	);

	const divisionLabel = settlement?.divisionAfter ?? hub?.rating.division.label ?? null;
	const divisionName = divisionLabel ? divisionLabel.split(' ')[0] : null;
	const rating = (() => {
		if (unranked) return { value: '—', color: duelHudColors.textSecondary };
		if (settlement?.ratingDelta !== null && settlement?.ratingDelta !== undefined) {
			return {
				value: formatSigned(settlement.ratingDelta),
				color: deltaColor(settlement.ratingDelta),
			};
		}
		return { value: formatScore(hub?.rating.rating ?? null), color: duelHudColors.textPrimary };
	})();
	const points = (() => {
		if (unranked) return { value: '—', color: duelHudColors.textSecondary };
		if (settlement?.seasonPointsDelta !== null && settlement?.seasonPointsDelta !== undefined) {
			return {
				value: formatSigned(settlement.seasonPointsDelta),
				color: deltaColor(settlement.seasonPointsDelta),
			};
		}
		return { value: formatScore(hub?.season.points ?? null), color: duelHudColors.textPrimary };
	})();

	return (
		<Card sizing={sizing}>
			<SectionTitle text={duelText(t, 'T_SITE_DUELS_RESULT_PROGRESSION')} sizing={sizing} />
			<View style={[styles.row, { columnGap: 6 * s, alignItems: 'stretch' }]}>
				<Tile
					sizing={sizing}
					icon={icon(DuelResultRatingPng)}
					value={rating.value}
					valueColor={rating.color}
					label={duelText(t, 'T_SITE_DUELS_DUEL_RATING')}
					caption={pendingCaption}
				/>
				<Tile
					sizing={sizing}
					icon={icon(divisionBadge(divisionName))}
					value={!unranked && divisionName ? divisionName.toUpperCase() : '—'}
					valueSize={14}
					valueColor={GOLD_TEXT}
					label={duelText(t, 'T_SITE_DUELS_RESULT_DIVISION')}
					caption={pendingCaption}
				/>
				<Tile
					sizing={sizing}
					icon={icon(DuelResultSeasonPng)}
					value={points.value}
					valueColor={points.color}
					label={duelText(t, 'T_SITE_DUELS_SEASON_POINTS')}
					caption={pendingCaption}
				/>
			</View>
			{unranked ? (
				<Text
					style={[
						styles.medium,
						styles.centeredText,
						{ color: duelHudColors.textSecondary, fontSize: sizing.f(11) },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_RESULT_UNRANKED')}
				</Text>
			) : null}
		</Card>
	);
};

const Actions = ({
	result,
	sizing,
	stacked,
	onClose,
	onSettled,
}: Pick<PanelProps, 'result' | 'sizing' | 'onClose' | 'onSettled'> & { stacked: boolean }) => {
	const { t } = useTranslation();
	const s = sizing.s;
	const settledReady = !!result && (!!result.settlement || !result.provisional);
	const radius = 12 * s;
	const buttonStyle = {
		height: 44 * s,
		borderRadius: radius,
		borderWidth: Math.max(1, s),
		paddingHorizontal: 12 * s,
		columnGap: 8 * s,
	};
	return (
		<View
			style={[
				stacked ? styles.column : styles.row,
				{ columnGap: 10 * s, rowGap: 8 * s, justifyContent: 'flex-end' },
			]}
		>
			<TouchableOpacity
				onPress={onClose}
				accessibilityRole='button'
				style={[styles.row, styles.centered, buttonStyle, { borderColor: GOLD }]}
			>
				<GradientFill stops={DUEL_HUD_COUNTER_FILL} radius={radius} />
				<View pointerEvents='none'>
					<DuelResultHomeSvg width={18 * s} height={18 * s} />
				</View>
				<Text
					style={[
						styles.bold,
						{ color: GOLD_TEXT, fontSize: sizing.f(14), letterSpacing: 0.8 * s },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_RESULT_RETURN_LOBBY')}
				</Text>
			</TouchableOpacity>
			<TouchableOpacity
				onPress={onSettled}
				disabled={!settledReady}
				accessibilityRole='button'
				accessibilityState={{ disabled: !settledReady }}
				style={[
					styles.row,
					styles.centered,
					buttonStyle,
					{ borderColor: duelHudColors.historyBorder, opacity: settledReady ? 1 : 0.45 },
				]}
			>
				<GradientFill stops={DUEL_HUD_HISTORY_FILL} radius={radius} />
				<View pointerEvents='none'>
					<DuelResultChartSvg width={18 * s} height={18 * s} />
				</View>
				<Text
					style={[
						styles.bold,
						{ color: duelHudColors.textPrimary, fontSize: sizing.f(14), letterSpacing: 0.8 * s },
					]}
				>
					{duelText(t, 'T_SITE_DUELS_RESULT_VIEW_SETTLED')}
				</Text>
			</TouchableOpacity>
		</View>
	);
};

const PanelStatus = ({
	errorKey,
	sizing,
	onRetry,
}: Pick<PanelProps, 'errorKey' | 'sizing' | 'onRetry'>) => {
	const { t } = useTranslation();
	if (!errorKey) {
		return (
			<View style={[styles.centered, { paddingVertical: 24 * sizing.s }]}>
				<ActivityIndicator color={duelHudColors.accent} />
			</View>
		);
	}
	return (
		<View style={[styles.centered, { paddingVertical: 16 * sizing.s, rowGap: 10 * sizing.s }]}>
			<Text
				style={[
					styles.semibold,
					styles.centeredText,
					{ color: duelHudColors.textSecondary, fontSize: sizing.f(13) },
				]}
			>
				{duelText(t, errorKey)}
			</Text>
			<TouchableOpacity onPress={onRetry} accessibilityRole='button'>
				<Text style={[styles.bold, { color: GOLD_TEXT, fontSize: sizing.f(14) }]}>
					{duelText(t, 'T_SITE_DUELS_RETRY')}
				</Text>
			</TouchableOpacity>
		</View>
	);
};

const Shell = ({
	children,
	sizing,
	style,
}: {
	children: React.ReactNode;
	sizing: Sizing;
	style: object;
}) => {
	const radius = DUEL_RESULT_LAYOUT.radius * sizing.s;
	return (
		<View
			style={[
				styles.shell,
				{
					borderRadius: radius,
					borderWidth: shellBorder(sizing.s),
					shadowRadius: 16 * sizing.s,
				},
				style,
			]}
		>
			<GradientFill stops={DUEL_HUD_SHELL_FILL} radius={radius} />
			{children}
		</View>
	);
};

type OverlayProps = {
	duelId: string;
	visible: boolean;
	onClose: () => void;
	onSettled: () => void;
	onHistory: () => void;
	onBreakdown: () => void;
};

export const DuelRunCompleteOverlay = ({
	duelId,
	visible,
	onClose,
	onSettled,
	onHistory,
	onBreakdown,
}: OverlayProps) => {
	const dispatch = useDispatch();
	const result = useSelector(selectDuelResult(duelId)) ?? null;
	const errorKey = useSelector(selectDuelResultErrorKey(duelId));
	const hub = useSelector(selectDuelsHub);
	const layout = useDuelHudLayout();
	const L = DUEL_RESULT_LAYOUT;

	useEffect(() => {
		dispatch(duelResultFocus(duelId));
		return () => {
			dispatch(duelResultBlur(duelId));
		};
	}, [dispatch, duelId]);

	if (!visible) return null;

	const { width, height, insets } = layout;
	const panelProps = {
		result,
		errorKey,
		hub,
		onRetry: () => dispatch(duelResultRetry(duelId)),
		onClose,
		onSettled,
		onHistory,
		onBreakdown,
	};
	const ready = !!result;

	let body: React.ReactNode;
	if (!layout.landscape) {
		const sizing: Sizing = { s: 1, f: size => size };
		const panelWidth = Math.min(L.portraitWidth, width - 2 * L.portraitSide);
		const top = insets.top + 2;
		const maxHeight = height - top - insets.bottom - L.portraitSide;
		body = (
			<Shell
				sizing={sizing}
				style={{
					position: 'absolute',
					top,
					left: (width - panelWidth) / 2,
					width: panelWidth,
					maxHeight,
				}}
			>
				<ScrollView contentContainerStyle={{ padding: 14, rowGap: 12 }}>
					<Header {...panelProps} sizing={sizing} />
					{ready ? (
						<>
							<Leaderboard {...panelProps} sizing={sizing} />
							<Breakdown {...panelProps} sizing={sizing} />
							<Progression {...panelProps} sizing={sizing} />
						</>
					) : (
						<PanelStatus {...panelProps} sizing={sizing} />
					)}
					<Actions {...panelProps} sizing={sizing} stacked />
				</ScrollView>
			</Shell>
		);
	} else {
		const desktop = !layout.phoneLandscape;
		const designWidth = desktop ? L.desktopWidth : L.landscapeWidth;
		const designHeight = desktop ? L.desktopHeight : L.landscapeHeight;
		const base = desktop ? 1.5 : 1;
		const safeLeft = Math.max(insets.left, L.landscapeMargin);
		const safeRight = Math.max(insets.right, L.landscapeMargin);
		const safeTop = Math.max(insets.top, L.landscapeMargin);
		const safeBottom = Math.max(insets.bottom, L.landscapeMargin);
		const availWidth = width - safeLeft - safeRight;
		const availHeight = height - safeTop - safeBottom;
		const fit = Math.min(1, availWidth / designWidth, availHeight / designHeight);
		const s = base * fit;
		const sizing: Sizing = { s, f: size => snapDesktopFont(size, s) };
		const panelWidth = designWidth * fit;
		const panelHeight = designHeight * fit;
		const gap = 14 * s;
		const columnsWidth = panelWidth - 3 * gap - 2 * shellBorder(s);
		const leftWidth = columnsWidth * 0.46;
		const rightWidth = columnsWidth - leftWidth;
		body = (
			<Shell
				sizing={sizing}
				style={{
					position: 'absolute',
					left: safeLeft + (availWidth - panelWidth) / 2,
					top: safeTop + (availHeight - panelHeight) / 2,
					width: panelWidth,
					height: panelHeight,
				}}
			>
				<View
					style={[styles.row, styles.grow, { padding: gap, columnGap: gap, alignItems: 'stretch' }]}
				>
					<ScrollView style={{ width: leftWidth }} contentContainerStyle={{ rowGap: 10 * s }}>
						<Header {...panelProps} sizing={sizing} />
						{ready ? <Leaderboard {...panelProps} sizing={sizing} /> : null}
					</ScrollView>
					<View style={{ width: rightWidth, rowGap: 10 * s }}>
						<ScrollView style={styles.grow} contentContainerStyle={{ rowGap: 10 * s }}>
							{ready ? (
								<>
									<Breakdown {...panelProps} sizing={sizing} />
									<Progression {...panelProps} sizing={sizing} />
								</>
							) : (
								<PanelStatus {...panelProps} sizing={sizing} />
							)}
						</ScrollView>
						<Actions {...panelProps} sizing={sizing} stacked={false} />
					</View>
				</View>
			</Shell>
		);
	}

	const content = <View style={styles.backdrop}>{body}</View>;

	if (Platform.OS === 'web') return <Portal>{content}</Portal>;

	return (
		<Modal
			transparent
			visible
			animationType='fade'
			onRequestClose={onClose}
			statusBarTranslucent
			navigationBarTranslucent
			supportedOrientations={DUEL_MODAL_ORIENTATIONS}
		>
			{content}
		</Modal>
	);
};

const styles = StyleSheet.create({
	backdrop: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: DUEL_RESULT_BACKDROP,
		zIndex: 10000,
		elevation: 10000,
	},
	shell: {
		overflow: 'hidden',
		borderColor: duelHudColors.stroke,
		shadowColor: 'rgba(0,168,255,0.45)',
		shadowOpacity: 1,
		shadowOffset: { width: 0, height: 0 },
	},
	card: { borderColor: duelHudColors.counterBorder, overflow: 'hidden' },
	tile: {
		flex: 1,
		borderColor: duelHudColors.historyBorder,
		overflow: 'hidden',
		alignItems: 'center',
	},
	pill: { alignSelf: 'flex-start', borderRadius: 999, flexShrink: 1, maxWidth: '100%' },
	row: { flexDirection: 'row', alignItems: 'center' },
	column: { flexDirection: 'column' },
	centered: { alignItems: 'center', justifyContent: 'center' },
	centeredText: { textAlign: 'center' },
	grow: { flex: 1 },
	shrink: { flexShrink: 1 },
	pushRight: { marginLeft: 'auto' },
	crown: { position: 'absolute', alignSelf: 'center' },
	absolute: { position: 'absolute' },
	black: { fontFamily: DUEL_HUD_FONT, fontWeight: '900' },
	bold: { fontFamily: DUEL_HUD_FONT, fontWeight: '700' },
	semibold: { fontFamily: DUEL_HUD_FONT, fontWeight: '600' },
	medium: { fontFamily: DUEL_HUD_FONT, fontWeight: '500' },
});
